import LoadingOverlay from "../../../LoadingOverlay";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BillableElement,
  BillOfMaterials,
  BOM_PROCESSING_STATES,
  PartPlacementCheckState,
} from "../../../../reducers/uploadedBoard";
import { InputLabel } from "../../../../constants/Styles";
import UpdateElementPlacement from "../../../ReviewBoardFlow/BillOfMaterials/SelectPart/UpdateElementPlacement";
import {
  AdvancementButton,
  ElementList,
  ElementListItem,
  PlacementToolContainer,
  Toolbar,
  ToolbarButtons,
} from "./PlacementChecksStyles";
import { PlacementCheckSummary } from "../../../ReviewBoardFlow/BillOfMaterials/ElementsTable/ElementsRow/PlacementCells";
import LoadingIcon from "../../../common/LoadingIcon";
import { elementShouldCheckPlacement } from "../../../../utils/placementChecks";
import Button from "../../../common/Button";
import RadioButtons from "../../../common/RadioButtons";
import { useBillOfMaterials } from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/billOfMaterials";
import { useRunPlacementChecks } from "../../../../utils/hooks/commonly-stored/uploaded-board/bomChecks";

function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

const ALL_STATES = "All";
const ONLY_PLACED_PARTS = "Only placed part";
type FilterType =
  | PartPlacementCheckState
  | typeof ALL_STATES
  | typeof ONLY_PLACED_PARTS;

function elementBelongsToFilter(
  element: BillableElement,
  filter: FilterType,
): boolean {
  if (filter === ALL_STATES) {
    return true;
  } else if (filter === ONLY_PLACED_PARTS) {
    return !element.do_not_populate;
  }
  return filter == element.part_placement_check.state;
}

export default function PlacementChecks() {
  const selectedElementRef = useRef<HTMLDivElement>(null);
  const billOfMaterials = useBillOfMaterials() as BillOfMaterials;
  const { checkPlacement, startingPlacementCheck } = useRunPlacementChecks();
  const [selectedFilter, setSelectedFilter] = useState<FilterType>(ALL_STATES);
  const [selectedElementIndex, setSelectedElementIndex] = useState(0);

  useEffect(() => {
    if (BOM_PROCESSING_STATES.has(billOfMaterials.state)) {
      setSelectedElementIndex(0);
      setSelectedFilter(ALL_STATES);
    }
  }, [billOfMaterials.state]);

  useEffect(() => {
    if (selectedElementRef.current !== null) {
      selectedElementRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedElementIndex]);

  const numElementsBelongingToFilter = billOfMaterials.elements.filter(
    (element) => elementBelongsToFilter(element, selectedFilter),
  ).length;

  const advanceToElement = useCallback(
    (startingAtIndex: number, forward: boolean) => {
      if (numElementsBelongingToFilter === 0) {
        setSelectedFilter(ALL_STATES);
        setSelectedElementIndex(0);
        return;
      }

      const step = forward ? 1 : -1;
      const length = billOfMaterials.elements.length;

      let currentIndex = mod(startingAtIndex, length);

      for (let i = 0; i < length; i++) {
        const element = billOfMaterials.elements[currentIndex]!;

        if (elementBelongsToFilter(element, selectedFilter)) {
          setSelectedElementIndex(currentIndex);
          return;
        }

        currentIndex = mod(currentIndex + step, length);
      }
    },
    [billOfMaterials.elements, numElementsBelongingToFilter, selectedFilter],
  );

  const onUpdatePlacement = () => {
    advanceToElement(selectedElementIndex + 1, true);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        advanceToElement(selectedElementIndex - 1, false);
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        advanceToElement(selectedElementIndex + 1, true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedElementIndex, advanceToElement]);

  if (BOM_PROCESSING_STATES.has(billOfMaterials.state)) {
    return (
      <LoadingIcon
        text={"Processing BOM..."}
        centerHorizontally
        centerVertically
      />
    );
  }

  const onChangePlacementStateFilter = (newFilterValue: string) => {
    setSelectedFilter(newFilterValue as FilterType);
    advanceToElement(0, true);
  };

  const selectedElement = billOfMaterials.elements[selectedElementIndex];

  return (
    <>
      <LoadingOverlay
        loading={startingPlacementCheck}
        text={"Starting checks..."}
      />
      <PlacementToolContainer>
        <Toolbar>
          <div>
            <InputLabel>Filter by</InputLabel>
            <RadioButtons
              options={[
                ALL_STATES,
                ONLY_PLACED_PARTS,
                ...(Object.values(
                  PartPlacementCheckState,
                ) as PartPlacementCheckState[]),
              ]
                .filter(
                  (state) =>
                    state != (PartPlacementCheckState.NEED_NOT_CHECK as string),
                )
                .map((state) => {
                  return {
                    state,
                    numElements: billOfMaterials.elements.filter((element) =>
                      elementBelongsToFilter(element, state as FilterType),
                    ).length,
                  };
                })
                .filter(({ numElements }) => numElements > 0)
                .map(({ state, numElements }) => {
                  return { label: `${state} (${numElements})`, value: state };
                })}
              currentValue={selectedFilter}
              onChange={onChangePlacementStateFilter}
            />
          </div>
          {billOfMaterials.elements.some(elementShouldCheckPlacement) && (
            <ToolbarButtons>
              <Button label={"Run placement checks"} onClick={checkPlacement} />
            </ToolbarButtons>
          )}
          <div>
            <InputLabel>Elements</InputLabel>
            <ElementList>
              {billOfMaterials.elements.map((element, elementIndex) => {
                if (elementBelongsToFilter(element, selectedFilter)) {
                  const selected = elementIndex == selectedElementIndex;
                  return (
                    <ElementListItem
                      key={element.id}
                      onClick={() => setSelectedElementIndex(elementIndex)}
                      selected={selected}
                      ref={selected ? selectedElementRef : null}
                    >
                      {element.name || "Unnamed"}
                      <PlacementCheckSummary element={element} />
                    </ElementListItem>
                  );
                }
              })}
            </ElementList>
          </div>
          <ToolbarButtons>
            <AdvancementButton
              label={"Prev"}
              onClick={() => advanceToElement(selectedElementIndex - 1, false)}
              isSmallSize
            />
            <AdvancementButton
              label={"Next"}
              onClick={() => advanceToElement(selectedElementIndex + 1, true)}
              isSmallSize
            />
          </ToolbarButtons>
        </Toolbar>
        {billOfMaterials.elements.length > 0 && selectedElement && (
          <UpdateElementPlacement
            selectedElement={selectedElement}
            onSubmit={onUpdatePlacement}
          />
        )}
      </PlacementToolContainer>
    </>
  );
}
